import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Building2, ChevronDown, X, Clock, Check } from 'lucide-react';

/**
 * OrganizationPicker - A searchable, keyboard-navigable organization selector
 *
 * Props:
 * - organizations: Array of organization objects with at least { id, name }
 * - value: Selected organization ID (single select) or array of IDs (multi-select)
 * - onChange: Callback when selection changes
 * - placeholder: Placeholder text when nothing is selected
 * - disabled: Disable the picker
 * - loading: Show loading state
 * - multiSelect: Allow multiple selections
 * - showDomain: Show organization domain in the list
 * - showUserCount: Show user count in the list
 * - recentOrgs: Array of recently accessed organization IDs
 * - maxRecentOrgs: Max number of recent orgs to show (default 5)
 * - className: Additional CSS classes
 * - error: Error message to display
 * - required: Mark as required field
 * - label: Label text
 */
const OrganizationPicker = ({
  organizations = [],
  value,
  onChange,
  placeholder = 'Select organization...',
  disabled = false,
  loading = false,
  multiSelect = false,
  showDomain = true,
  showUserCount = false,
  recentOrgs = [],
  maxRecentOrgs = 5,
  className = '',
  error = '',
  required = false,
  label = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Filter organizations based on search term
  const filteredOrgs = organizations.filter(org => {
    const searchLower = searchTerm.toLowerCase();
    return (
      org.name?.toLowerCase().includes(searchLower) ||
      org.domain?.toLowerCase().includes(searchLower) ||
      org.id?.toLowerCase().includes(searchLower)
    );
  });

  // Get recent organizations that match the filter
  const recentOrgsList = recentOrgs
    .slice(0, maxRecentOrgs)
    .map(id => organizations.find(org => org.id === id))
    .filter(Boolean)
    .filter(org => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        org.name?.toLowerCase().includes(searchLower) ||
        org.domain?.toLowerCase().includes(searchLower)
      );
    });

  // Get non-recent organizations
  const nonRecentOrgs = filteredOrgs.filter(
    org => !recentOrgs.includes(org.id)
  );

  // Combined list for keyboard navigation
  const allDisplayedOrgs = [...recentOrgsList, ...nonRecentOrgs];

  // Get selected organization(s) for display
  const getSelectedOrg = () => {
    if (multiSelect) {
      return (value || []).map(id => organizations.find(org => org.id === id)).filter(Boolean);
    }
    return organizations.find(org => org.id === value);
  };

  const selectedOrg = getSelectedOrg();

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedEl = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < allDisplayedOrgs.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (allDisplayedOrgs[highlightedIndex]) {
          handleSelect(allDisplayedOrgs[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  }, [isOpen, highlightedIndex, allDisplayedOrgs]);

  const handleSelect = (org) => {
    if (multiSelect) {
      const currentValue = value || [];
      const newValue = currentValue.includes(org.id)
        ? currentValue.filter(id => id !== org.id)
        : [...currentValue, org.id];
      onChange(newValue);
    } else {
      onChange(org.id);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(multiSelect ? [] : '');
  };

  const handleRemoveMultiItem = (e, orgId) => {
    e.stopPropagation();
    onChange((value || []).filter(id => id !== orgId));
  };

  const isSelected = (orgId) => {
    if (multiSelect) {
      return (value || []).includes(orgId);
    }
    return value === orgId;
  };

  const renderOrgItem = (org, index, isRecent = false) => {
    const selected = isSelected(org.id);
    const highlighted = index === highlightedIndex;

    return (
      <button
        key={org.id}
        data-index={index}
        type="button"
        onClick={() => handleSelect(org)}
        onMouseEnter={() => setHighlightedIndex(index)}
        className={`w-full px-3 py-2 text-left flex items-center justify-between transition-colors ${
          highlighted
            ? 'bg-blue-50 text-blue-900'
            : 'hover:bg-gray-50'
        } ${selected ? 'bg-blue-50' : ''}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isRecent && (
            <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">
              {org.name}
            </div>
            {(showDomain && org.domain) || (showUserCount && org.user_count !== undefined) ? (
              <div className="text-xs text-gray-500 truncate">
                {showDomain && org.domain && <span>{org.domain}</span>}
                {showDomain && org.domain && showUserCount && org.user_count !== undefined && (
                  <span className="mx-1">·</span>
                )}
                {showUserCount && org.user_count !== undefined && (
                  <span>{org.user_count} users</span>
                )}
              </div>
            ) : null}
          </div>
        </div>
        {selected && (
          <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
        )}
      </button>
    );
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 text-left border rounded-lg transition-colors ${
          disabled
            ? 'bg-gray-100 cursor-not-allowed text-gray-500'
            : isOpen
              ? 'border-blue-500 ring-2 ring-blue-200'
              : error
                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {loading ? (
            <span className="text-gray-400 text-sm">Loading...</span>
          ) : multiSelect && Array.isArray(selectedOrg) && selectedOrg.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedOrg.slice(0, 3).map(org => (
                <span
                  key={org.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
                >
                  {org.name}
                  <X
                    className="w-3 h-3 cursor-pointer hover:text-blue-600"
                    onClick={(e) => handleRemoveMultiItem(e, org.id)}
                  />
                </span>
              ))}
              {selectedOrg.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{selectedOrg.length - 3} more
                </span>
              )}
            </div>
          ) : !multiSelect && selectedOrg ? (
            <span className="text-sm text-gray-900 truncate">{selectedOrg.name}</span>
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {((multiSelect && Array.isArray(value) && value.length > 0) || (!multiSelect && value)) && !disabled && (
            <X
              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={handleClear}
            />
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search organizations..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {filteredOrgs.length} organization{filteredOrgs.length !== 1 ? 's' : ''}
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
          </div>

          {/* Options List */}
          <div ref={listRef} className="max-h-60 overflow-y-auto">
            {allDisplayedOrgs.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {searchTerm ? 'No organizations found' : 'No organizations available'}
              </div>
            ) : (
              <>
                {/* Recent Organizations Section */}
                {recentOrgsList.length > 0 && !searchTerm && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase bg-gray-50">
                      Recent
                    </div>
                    {recentOrgsList.map((org, idx) => renderOrgItem(org, idx, true))}
                  </>
                )}

                {/* All Organizations Section */}
                {nonRecentOrgs.length > 0 && (
                  <>
                    {recentOrgsList.length > 0 && !searchTerm && (
                      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase bg-gray-50">
                        All Organizations
                      </div>
                    )}
                    {nonRecentOrgs.map((org, idx) =>
                      renderOrgItem(org, recentOrgsList.length + idx, false)
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationPicker;
